import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import TextInput from '../../../../components/input';
import SelectSearch from '../../../../components/SelectSearch';
import LocationDropdownInput from '../../../../components/SelectSearch/LocationDropdown';
import useForm from '../../../../customHooks/useForm';
import { getCities, getCountries } from '../../../../utils/cities';
import { getPlaceDetails } from '../../../../utils/googleMaps';
import '../PersonalInfoEdit/PersonalInfoEdit.scss';
import StyledButton from '../../../../components/StyledButton';
import { UPDATE_USER_PROFILE } from '../../../../graphql/admin_mutations';
import useToast from '../../../../customHooks/useToast';

interface IResidentialDetailsEdit{
  payload:any;
  buttonState?:boolean;
  disableMode?: boolean
  fetchCustomer?: any;
  customerId: any;
}
const ResidentialDetailsEdit = ({
  payload, disableMode, customerId, fetchCustomer,
}:IResidentialDetailsEdit) => {
  const Toast = useToast();
  const [canEditPostalAndCity, setCanEditPostalAndCity] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [usePlainTextCity, setUsePlainTextCity] = useState<boolean>(false);

  const isAllowUpdate = payload?.hasProfileUpdateRequest;

  const history = useHistory();

  const {
    formValues,
    handleChange,
    resetForm,
  } = useForm(
    {
      location: '',
      zipCode: '',
      city: '',
      country: '',
    },
  );

  const [countries, setCountries]: any = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');

  const handleLocationChange = async (data: any) => {
    setCanEditPostalAndCity(true);
    resetForm({
      location: data?.target?.value,
    });

    setLoading(true);
    // make api call to get city and postal code
    const result: any = await getPlaceDetails(data?.target?.id);
    resetForm({ state: result?.state || ' ' });

    if (result?.postalCode && result?.city) {
      resetForm({ zipCode: result?.postalCode });
      resetForm({ city: result?.city });

      setUsePlainTextCity(true);
      setCanEditPostalAndCity(false);
    } else {
      resetForm({ zipCode: ' ' });
      resetForm({ city: ' ' });
      setUsePlainTextCity(false);
    }
    setLoading(false);
  };

  const getCountryList = async () => {
    try {
      const result = getCountries();
      return setCountries(result);
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    getCountryList();
  }, []);

  useEffect(() => {
    if (payload?.country && countries?.length) {
      const rec = countries?.find(
        (item: any) => item.value?.toLowerCase?.() === payload?.country?.toLowerCase?.(),
      );
      setSelectedCountryCode(rec?.id || '');
    }
  }, [payload, countries]);

  useEffect(() => {
    if (payload?.firstName) {
      resetForm({
        location: payload?.location,
        zipCode: payload?.zipCode,
        city: payload?.city,
        country: payload?.country,
      });
    }
  }, [payload]);

  const [updateUserProfile, { loading: updateUserProfileLoading }] = useMutation<
  { adminUpdateUserProfileCa: any },
  any
>(UPDATE_USER_PROFILE);

  const fullName = `${payload?.firstName} ${payload?.lastName}`;

  const handleSubmit = async () => {
    const input = {
      city: formValues?.city,
      location: formValues?.location,
      zipCode: formValues?.zipCode,
    };

    const variables = {
      input,
      userId: customerId,
    };

    if (!updateUserProfileLoading) {
      try {
        const res = await updateUserProfile({
          variables,
        });
        if (res?.data?.adminUpdateUserProfileCa?.status === 200) {
          fetchCustomer();
          Toast.success(res?.data?.adminUpdateUserProfileCa?.message || 'Operation successful');
        } else {
          Toast.error(res?.data?.adminUpdateUserProfileCa?.message || 'Operation Failed');
        }
      } catch (e: any) {
        if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
          Toast.error(e.graphQLErrors[0].message);
        } else {
          Toast.error('Something went wrong');
        }
      }
    }
  };

  return (
    <div className="edit-wrapper">
      <div className="edit-details">
        <div className="edit-wrapper-text">
          Residential Address of
          {' '}
          {fullName}
        </div>
        <div className="row">
          <div className="col-md-6 col-12" style={{ marginBottom: '20px' }}>
            <LocationDropdownInput
              onChange={handleLocationChange}
              value={formValues?.location}
              label="Address"
              disabled={disableMode}
              countryCode={selectedCountryCode}
              name="location"
            />
          </div>
          <div className="col-md-6 col-12" style={{ marginBottom: !usePlainTextCity && '20px' }}>
            {
              usePlainTextCity ? (
                <TextInput
                  customOnChange={handleChange}
                  name="city"
                  value={formValues?.city}
                  label="City"
                  disabled={loading || !canEditPostalAndCity}
                  width="100%"
                />
              ) : (
                <SelectSearch
                  options={getCities(payload?.country)}
                  placeholder="City"
                  name="city"
                  value={formValues?.city}
                  disabled={loading || !canEditPostalAndCity}
                  onChange={handleChange}
                />
              )
            }

          </div>
          <div className="col-md-6 col-12">
            <TextInput
              label="Zip Code"
              customOnChange={handleChange}
              name="zipCode"
              value={formValues?.zipCode}
              disabled={loading || !canEditPostalAndCity}
              width="100%"
            />
          </div>
          <div className="col-md-6 col-12" style={{ marginBottom: '20px' }}>
            <SelectSearch
              options={countries}
              placeholder="Country"
              name="country"
              value={formValues?.country}
              disabled
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="flexed-end">
          <div className="customer-update-btn-wrapper">
            <StyledButton
              name="CANCEL"
              backgroundColor="white"
              color="#000"
              padding="10px 24px"
              borderRadius="4px"
              border="1px solid #000"
              onClick={() => history?.goBack()}
              disabled={updateUserProfileLoading}
              marginRight="16px"
              height="32px"
              width="140px"
              fontSize="12px"
              fontWeight="700"
            />
          </div>
          <div className="customer-update-btn-wrapper">
            <StyledButton
              name={updateUserProfileLoading ? 'Please wait...' : 'SAVE CHANGES'}
              backgroundColor={isAllowUpdate ? '#5941A9' : '#f5f5f5'}
              color={isAllowUpdate ? 'white' : '#C4C4C4'}
              padding="10px 24px"
              borderRadius="4px"
              border="none"
              height="32px"
              fontSize="12px"
              fontWeight="700"
              onClick={handleSubmit}
              disabled={!isAllowUpdate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default ResidentialDetailsEdit;
